import { useRef, useEffect } from 'react';
import Editor, { Monaco } from '@monaco-editor/react';
import * as monaco from 'monaco-editor';

interface CodeEditorProps {
  value: string;
  onChange?: (value: string | undefined) => void;
  language?: string;
  readOnly?: boolean;
  height?: string;
  theme?: string;
}

export function CodeEditor({
  value,
  onChange,
  language = 'javascript',
  readOnly = false,
  height = '500px',
  theme = 'vs-dark-custom',
}: CodeEditorProps) {
  const editorRef = useRef<monaco.editor.IStandaloneCodeEditor | null>(null);

  function handleEditorDidMount(
    editor: monaco.editor.IStandaloneCodeEditor,
    monaco: Monaco
  ) {
    editorRef.current = editor;

    // Define custom dark theme
    monaco.editor.defineTheme('vs-dark-custom', {
      base: 'vs-dark',
      inherit: true,
      rules: [
        { token: 'comment', foreground: '6A9955', fontStyle: 'italic' },
        { token: 'keyword', foreground: '22c55e', fontStyle: 'bold' },
        { token: 'string', foreground: '4ade80' },
        { token: 'number', foreground: '86efac' },
        { token: 'function', foreground: '22c55e' },
        { token: 'variable', foreground: 'dcfce7' },
      ],
      colors: {
        'editor.background': '#000000',
        'editor.foreground': '#dcfce7',
        'editor.lineHighlightBackground': '#18181b',
        'editor.selectionBackground': '#16a34a40',
        'editorCursor.foreground': '#22c55e',
        'editorLineNumber.foreground': '#3f3f46',
        'editorLineNumber.activeForeground': '#22c55e',
        'editor.inactiveSelectionBackground': '#16a34a20',
        'editorWhitespace.foreground': '#3f3f46',
      },
    });

    monaco.editor.setTheme('vs-dark-custom');
  }

  return (
    <div className="rounded-lg overflow-hidden border border-dark-200 shadow-[0_0_20px_rgba(34,197,94,0.1)]">
      <Editor
        height={height}
        language={language}
        value={value}
        onChange={onChange}
        onMount={handleEditorDidMount}
        theme={theme}
        options={{
          readOnly,
          minimap: { enabled: false },
          fontSize: 14,
          lineNumbers: 'on',
          roundedSelection: false,
          scrollBeyondLastLine: false,
          scrollbar: {
            alwaysConsumeMouseWheel: false,
          },
          automaticLayout: true,
          tabSize: 2,
          wordWrap: 'on',
          padding: { top: 16, bottom: 16 },
          fontFamily: 'JetBrains Mono, Fira Code, monospace',
          fontLigatures: true,
          cursorBlinking: 'smooth',
          cursorSmoothCaretAnimation: 'on',
          smoothScrolling: true,
          renderLineHighlight: 'all',
          renderWhitespace: 'selection',
          bracketPairColorization: {
            enabled: true,
          },
        }}
      />
    </div>
  );
}
